import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, ShieldCheck, ArrowLeft, ArrowRight, Eye, EyeOff, CheckCircle2, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { PageMasthead } from "@/components/ui-custom/PageMasthead";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const DAERAH_LIST = ["Kuching", "Samarahan", "Sri Aman", "Sibu", "Bintulu", "Miri", "Lain-lain"];
const HUBUNGAN_LIST = ["Suami", "Isteri", "Anak", "Ibu", "Ayah", "Adik-beradik", "Saudara", "Lain-lain"];
const EDUCATION_LIST = ["UPSR", "PMR/PT3", "SPM", "STPM", "Sijil", "Diploma", "Ijazah Sarjana Muda", "Sarjana", "Lain-lain"];
const CURRENT_YEAR = new Date().getFullYear();
const SESSION_YEARS = Array.from({ length: CURRENT_YEAR + 1 - 2010 + 1 }, (_, i) => String(CURRENT_YEAR + 1 - i));

const STEP_LABELS = ["Cari Rekod", "Emel & Kata Laluan", "Maklumat Ahli", "Maklumat Kursus", "Maklumat Waris", "Pengesahan"];

const STEP_FIELDS = {
  3: ["gender", "address", "daerah", "education_level", "occupation"],
  4: ["course_name", "course_date", "course_session_year", "course_venue"],
  5: ["next_of_kin_name", "next_of_kin_relationship", "next_of_kin_phone"],
};

export default function TuntutKeahlian() {
  const [step, setStep] = useState(1);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [record, setRecord] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const [searchForm, setSearchForm] = useState({ identification_number: "" });
  const [form, setForm] = useState({ email: "", phone: "+60", password: "", confirm: "", next_of_kin_phone: "+60", declaration_accepted: false, data_agreement_accepted: false });

  const updateField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const runSearch = async (e) => {
    e.preventDefault();
    setSearching(true);
    setSearchError("");
    try {
      const res = await api.post("/public/claim/search", searchForm);
      setRecord(res.data);
      setForm((f) => ({ ...f, email: res.data.email || "", phone: res.data.phone || "+60" }));
      setStep(2);
    } catch (e) {
      setSearchError(formatApiError(e.response?.data?.detail) || "Rekod tidak dijumpai");
    } finally {
      setSearching(false);
    }
  };

  const goToProfileSteps = (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Kata laluan mesti sekurang-kurangnya 8 aksara");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("Kata laluan tidak sepadan");
      return;
    }
    setStep(3);
  };

  const validateStep = (s) => {
    const fields = STEP_FIELDS[s];
    if (!fields) return true;
    const nextErrors = {};
    fields.forEach((f) => {
      if (!form[f] || String(form[f]).trim() === "") nextErrors[f] = "Ruangan ini wajib diisi";
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      toast.error("Sila lengkapkan semua ruangan wajib sebelum meneruskan");
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (!validateStep(step)) return;
    setStep(step + 1);
  };

  const submitClaim = async (e) => {
    e.preventDefault();
    if (!form.declaration_accepted || !form.data_agreement_accepted) {
      toast.error("Sila tandakan kedua-dua perakuan sebelum menghantar");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/claim/submit", {
        member_id: record.member_id,
        identification_number: searchForm.identification_number,
        email: form.email,
        password: form.password,
        phone: form.phone,
        gender: form.gender,
        address: form.address,
        daerah: form.daerah,
        education_level: form.education_level,
        occupation: form.occupation,
        course_name: form.course_name,
        course_date: form.course_date,
        course_session_year: form.course_session_year,
        course_venue: form.course_venue,
        next_of_kin_name: form.next_of_kin_name,
        next_of_kin_relationship: form.next_of_kin_relationship,
        next_of_kin_phone: form.next_of_kin_phone,
      });
      setStep(7);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="tuntut-keahlian-page">
      <PageMasthead
        breadcrumb="TUNTUT KEAHLIAN"
        title="Tuntut Keahlian Anda"
        subtitle="Untuk ahli PPPS sedia ada dari rekod pendaftaran lama. Sahkan No. Kad Pengenalan anda, lengkapkan maklumat, dan tetapkan emel & kata laluan untuk log masuk."
      />

      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {step >= 2 && step <= 6 && (
          <div className="flex items-center justify-between mb-10" data-testid="claim-progress-indicator">
            {STEP_LABELS.slice(1).map((label, i) => (
              <div key={label} className="flex-1 flex flex-col items-center relative">
                {i > 0 && <div className={`absolute top-3 right-1/2 w-full h-0.5 -z-10 ${step > i + 1 ? "bg-piks-gold" : "bg-piks-border"}`} />}
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold z-10 ${step > i + 2 ? "bg-piks-gold text-piks-black" : step === i + 2 ? "bg-piks-black text-white" : "bg-piks-border text-piks-muted"}`}>
                  {step > i + 2 ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </div>
                <span className="text-[10px] text-piks-muted mt-2 text-center hidden sm:block">{label}</span>
              </div>
            ))}
          </div>
        )}

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.form
              key="step1"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              onSubmit={runSearch}
              className="space-y-5 bg-white p-7 rounded-xl border border-piks-border max-w-xl mx-auto"
              data-testid="claim-search-form"
            >
              <div>
                <Label>No. Kad Pengenalan (IC)</Label>
                <Input
                  data-testid="claim-ic-input"
                  required
                  placeholder="contoh: 901111-13-6903"
                  value={searchForm.identification_number}
                  onChange={(e) => setSearchForm((f) => ({ ...f, identification_number: e.target.value }))}
                  className="mt-1.5"
                />
              </div>
              {searchError && <p className="text-sm text-piks-red" data-testid="claim-search-error">{searchError}</p>}
              <Button type="submit" disabled={searching} data-testid="claim-search-submit-btn" className="w-full h-11 bg-piks-black hover:bg-piks-black/90">
                <Search className="w-4 h-4 mr-2" /> {searching ? "Mencari..." : "Cari Rekod Saya"}
              </Button>
              <p className="text-xs text-piks-muted text-center">Tidak jumpa rekod anda? Hubungi urusetia PPPS untuk bantuan.</p>
            </motion.form>
          )}

          {step === 2 && record && (
            <motion.div key="step2" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="bg-white p-7 rounded-xl border border-piks-border max-w-xl mx-auto" data-testid="claim-credentials-form">
              <button onClick={() => setStep(1)} className="flex items-center gap-1.5 text-sm text-piks-muted hover:text-piks-black mb-5" data-testid="claim-back-btn">
                <ArrowLeft className="w-4 h-4" /> Kembali
              </button>

              <div className="flex items-center gap-2.5 mb-6 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                <div>
                  <p className="font-semibold text-piks-black text-sm">Rekod dijumpai!</p>
                  <p className="text-xs text-piks-muted">{record.full_name} — {record.category_name}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                <div>
                  <p className="text-xs text-piks-muted mb-0.5">No. Ahli Baharu</p>
                  <p className="font-mono font-semibold text-piks-black" data-testid="claim-new-member-number">{record.member_number}</p>
                </div>
                {record.legacy_member_number && (
                  <div>
                    <p className="text-xs text-piks-muted mb-0.5">No. Ahli Lama</p>
                    <p className="font-mono text-piks-muted">{record.legacy_member_number}</p>
                  </div>
                )}
              </div>

              <form onSubmit={goToProfileSteps} className="space-y-4">
                <div>
                  <Label>Emel (untuk log masuk)</Label>
                  <Input data-testid="claim-email-input" type="email" required value={form.email} onChange={(e) => updateField("email", e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label>No. Telefon</Label>
                  <Input data-testid="claim-phone-input" value={form.phone} onChange={(e) => updateField("phone", e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label>Kata Laluan</Label>
                  <div className="relative mt-1.5">
                    <Input data-testid="claim-password-input" type={showPassword ? "text" : "password"} required minLength={8} value={form.password} onChange={(e) => updateField("password", e.target.value)} />
                    <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-piks-muted" data-testid="claim-toggle-password-visibility">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>Sahkan Kata Laluan</Label>
                  <Input data-testid="claim-password-confirm-input" type={showPassword ? "text" : "password"} required minLength={8} value={form.confirm} onChange={(e) => updateField("confirm", e.target.value)} className="mt-1.5" />
                </div>
                <Button type="submit" data-testid="claim-continue-btn" className="w-full h-11 bg-piks-black hover:bg-piks-black/90">
                  Seterusnya <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </form>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="step3" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="bg-white p-7 rounded-xl border border-piks-border" data-testid="claim-step-3">
              <h2 className="font-semibold text-piks-black mb-4">Maklumat Ahli</h2>
              <div className="space-y-4">
                <div>
                  <Label>Jantina <span className="text-piks-red">*</span></Label>
                  <Select value={form.gender} onValueChange={(v) => updateField("gender", v)}>
                    <SelectTrigger data-testid="select-gender" className="mt-1.5 bg-white"><SelectValue placeholder="Pilih jantina" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Lelaki">Lelaki</SelectItem>
                      <SelectItem value="Perempuan">Perempuan</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.gender && <p className="text-xs text-piks-red mt-1" data-testid="error-gender">{errors.gender}</p>}
                </div>
                <div>
                  <Label>Alamat <span className="text-piks-red">*</span></Label>
                  <Textarea data-testid="input-address" value={form.address || ""} onChange={(e) => updateField("address", e.target.value)} className="mt-1.5" />
                  {errors.address && <p className="text-xs text-piks-red mt-1" data-testid="error-address">{errors.address}</p>}
                </div>
                <div>
                  <Label>Daerah <span className="text-piks-red">*</span></Label>
                  <Select value={form.daerah} onValueChange={(v) => updateField("daerah", v)}>
                    <SelectTrigger data-testid="select-daerah" className="mt-1.5 bg-white"><SelectValue placeholder="Pilih daerah" /></SelectTrigger>
                    <SelectContent>
                      {DAERAH_LIST.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {errors.daerah && <p className="text-xs text-piks-red mt-1" data-testid="error-daerah">{errors.daerah}</p>}
                </div>
                <div>
                  <Label>Tahap Pendidikan <span className="text-piks-red">*</span></Label>
                  <Select value={form.education_level} onValueChange={(v) => updateField("education_level", v)}>
                    <SelectTrigger data-testid="select-education-level" className="mt-1.5 bg-white"><SelectValue placeholder="Pilih tahap pendidikan" /></SelectTrigger>
                    <SelectContent>
                      {EDUCATION_LIST.map((ed) => <SelectItem key={ed} value={ed}>{ed}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {errors.education_level && <p className="text-xs text-piks-red mt-1" data-testid="error-education_level">{errors.education_level}</p>}
                </div>
                <div>
                  <Label>Pekerjaan <span className="text-piks-red">*</span></Label>
                  <Input data-testid="input-occupation" value={form.occupation || ""} onChange={(e) => updateField("occupation", e.target.value)} className="mt-1.5" />
                  {errors.occupation && <p className="text-xs text-piks-red mt-1" data-testid="error-occupation">{errors.occupation}</p>}
                </div>
              </div>
              <StepNav onBack={() => setStep(2)} onNext={nextStep} />
            </motion.div>
          )}

          {step === 4 && (
            <motion.div key="step4" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="bg-white p-7 rounded-xl border border-piks-border" data-testid="claim-step-4">
              <h2 className="font-semibold text-piks-black mb-4">Maklumat Kursus</h2>
              <div className="space-y-4">
                <div>
                  <Label>Nama Kursus <span className="text-piks-red">*</span></Label>
                  <Input data-testid="input-course_name" value={form.course_name || ""} onChange={(e) => updateField("course_name", e.target.value)} className="mt-1.5" />
                  {errors.course_name && <p className="text-xs text-piks-red mt-1" data-testid="error-course_name">{errors.course_name}</p>}
                </div>
                <div>
                  <Label>Tarikh Kursus <span className="text-piks-red">*</span></Label>
                  <Input type="date" data-testid="input-course_date" value={form.course_date || ""} onChange={(e) => updateField("course_date", e.target.value)} className="mt-1.5" />
                  {errors.course_date && <p className="text-xs text-piks-red mt-1" data-testid="error-course_date">{errors.course_date}</p>}
                </div>
                <div>
                  <Label>Sesi / Tahun <span className="text-piks-red">*</span></Label>
                  <Select value={form.course_session_year} onValueChange={(v) => updateField("course_session_year", v)}>
                    <SelectTrigger data-testid="select-course-session-year" className="mt-1.5 bg-white"><SelectValue placeholder="Pilih tahun" /></SelectTrigger>
                    <SelectContent>
                      {SESSION_YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {errors.course_session_year && <p className="text-xs text-piks-red mt-1" data-testid="error-course_session_year">{errors.course_session_year}</p>}
                </div>
                <div>
                  <Label>Tempat Kursus <span className="text-piks-red">*</span></Label>
                  <Input data-testid="input-course_venue" value={form.course_venue || ""} onChange={(e) => updateField("course_venue", e.target.value)} className="mt-1.5" />
                  {errors.course_venue && <p className="text-xs text-piks-red mt-1" data-testid="error-course_venue">{errors.course_venue}</p>}
                </div>
              </div>
              <StepNav onBack={() => setStep(3)} onNext={nextStep} />
            </motion.div>
          )}

          {step === 5 && (
            <motion.div key="step5" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="bg-white p-7 rounded-xl border border-piks-border" data-testid="claim-step-5">
              <h2 className="font-semibold text-piks-black mb-4">Maklumat Waris</h2>
              <div className="space-y-4">
                <div>
                  <Label>Nama Waris <span className="text-piks-red">*</span></Label>
                  <Input data-testid="input-next_of_kin_name" value={form.next_of_kin_name || ""} onChange={(e) => updateField("next_of_kin_name", e.target.value)} className="mt-1.5" />
                  {errors.next_of_kin_name && <p className="text-xs text-piks-red mt-1" data-testid="error-next_of_kin_name">{errors.next_of_kin_name}</p>}
                </div>
                <div>
                  <Label>Hubungan <span className="text-piks-red">*</span></Label>
                  <Select value={form.next_of_kin_relationship} onValueChange={(v) => updateField("next_of_kin_relationship", v)}>
                    <SelectTrigger data-testid="select-next-of-kin-relationship" className="mt-1.5 bg-white"><SelectValue placeholder="Pilih hubungan" /></SelectTrigger>
                    <SelectContent>
                      {HUBUNGAN_LIST.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {errors.next_of_kin_relationship && <p className="text-xs text-piks-red mt-1" data-testid="error-next_of_kin_relationship">{errors.next_of_kin_relationship}</p>}
                </div>
                <div>
                  <Label>No. Telefon Waris <span className="text-piks-red">*</span></Label>
                  <Input data-testid="input-next_of_kin_phone" value={form.next_of_kin_phone || "+60"} onChange={(e) => updateField("next_of_kin_phone", e.target.value)} className="mt-1.5" />
                  {errors.next_of_kin_phone && <p className="text-xs text-piks-red mt-1" data-testid="error-next_of_kin_phone">{errors.next_of_kin_phone}</p>}
                </div>
              </div>
              <StepNav onBack={() => setStep(4)} onNext={nextStep} />
            </motion.div>
          )}

          {step === 6 && (
            <motion.div key="step6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="bg-white p-7 rounded-xl border border-piks-border" data-testid="claim-step-6">
              <h2 className="font-semibold text-piks-black mb-4">Pengesahan</h2>
              <div className="bg-piks-ivory rounded-lg p-4 text-sm text-piks-muted space-y-1.5 mb-5" data-testid="claim-summary">
                <p className="font-semibold text-piks-black text-xs uppercase tracking-wide mb-1">Maklumat Ahli</p>
                <p><strong className="text-piks-black">Nama:</strong> {record?.full_name}</p>
                <p><strong className="text-piks-black">No. Ahli Baharu:</strong> {record?.member_number}</p>
                <p><strong className="text-piks-black">Jantina:</strong> {form.gender || "-"}</p>
                <p><strong className="text-piks-black">Alamat:</strong> {form.address}</p>
                <p><strong className="text-piks-black">Daerah:</strong> {form.daerah}</p>
                <p><strong className="text-piks-black">Emel:</strong> {form.email}</p>
                <p><strong className="text-piks-black">Telefon:</strong> {form.phone}</p>
                <p><strong className="text-piks-black">Tahap Pendidikan:</strong> {form.education_level}</p>
                <p><strong className="text-piks-black">Pekerjaan:</strong> {form.occupation}</p>
                <p className="font-semibold text-piks-black text-xs uppercase tracking-wide mb-1 pt-2">Maklumat Kursus</p>
                <p><strong className="text-piks-black">Kursus:</strong> {form.course_name}</p>
                <p><strong className="text-piks-black">Tarikh:</strong> {form.course_date}</p>
                <p><strong className="text-piks-black">Sesi/Tahun:</strong> {form.course_session_year}</p>
                <p><strong className="text-piks-black">Tempat:</strong> {form.course_venue}</p>
                <p className="font-semibold text-piks-black text-xs uppercase tracking-wide mb-1 pt-2">Maklumat Waris</p>
                <p><strong className="text-piks-black">Nama Waris:</strong> {form.next_of_kin_name}</p>
                <p><strong className="text-piks-black">Hubungan:</strong> {form.next_of_kin_relationship}</p>
                <p><strong className="text-piks-black">Telefon Waris:</strong> {form.next_of_kin_phone}</p>
              </div>

              <form onSubmit={submitClaim} className="space-y-5">
                <div className="flex items-start gap-3">
                  <Checkbox id="declaration" data-testid="claim-declaration-checkbox" checked={!!form.declaration_accepted} onCheckedChange={(v) => updateField("declaration_accepted", !!v)} className="mt-0.5" />
                  <Label htmlFor="declaration" className="text-sm leading-relaxed text-piks-black">
                    Saya mengesahkan bahawa semua maklumat yang diberikan adalah benar dan tepat.
                  </Label>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox id="data_agreement" data-testid="claim-data-agreement-checkbox" checked={!!form.data_agreement_accepted} onCheckedChange={(v) => updateField("data_agreement_accepted", !!v)} className="mt-0.5" />
                  <Label htmlFor="data_agreement" className="text-sm leading-relaxed text-piks-black">
                    Saya bersetuju dengan penggunaan data peribadi saya mengikut terma keahlian dan perlembagaan PPPS.
                  </Label>
                </div>
                <div className="flex flex-wrap justify-between gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setStep(5)} data-testid="claim-step6-back-btn">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Kembali
                  </Button>
                  <Button type="submit" disabled={submitting || !form.declaration_accepted || !form.data_agreement_accepted} data-testid="claim-submit-btn" className="bg-piks-red hover:bg-piks-red/90 text-white">
                    {submitting ? "Menghantar..." : "Tuntut Keahlian Saya"}
                  </Button>
                </div>
              </form>
            </motion.div>
          )}

          {step === 7 && (
            <motion.div key="step7" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white p-8 rounded-xl border border-piks-border text-center max-w-xl mx-auto" data-testid="claim-success-view">
              <div className="w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-piks-black mb-2">Tuntutan Berjaya Dihantar!</h2>
              <p className="text-sm text-piks-muted mb-6">
                Akaun anda telah dicipta untuk No. Ahli <span className="font-mono font-semibold text-piks-black">{record?.member_number}</span>.
                Sila tunggu pengesahan daripada admin PPPS. Setelah diluluskan, sila log masuk menggunakan emel &amp; kata laluan yang baru ditetapkan.
              </p>
              <Button asChild data-testid="claim-goto-login-btn" className="w-full h-11 bg-piks-black hover:bg-piks-black/90">
                <Link to="/login">Sila Log Masuk</Link>
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  );
}

function StepNav({ onBack, onNext }) {
  return (
    <div className="flex flex-wrap justify-between gap-3 pt-6 mt-2 border-t border-piks-border">
      <Button type="button" variant="outline" onClick={onBack} data-testid="claim-step-back-btn">
        <ArrowLeft className="w-4 h-4 mr-1" /> Kembali
      </Button>
      <Button type="button" onClick={onNext} data-testid="claim-step-next-btn" className="bg-piks-black hover:bg-piks-black/90">
        Seterusnya <ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>
    </div>
  );
}
